import React from "react";
import SoftBox from "../SoftBox";

/**
 * The dashboard card header — one strip, one rule, everywhere.
 *
 * These exact values were already the house standard: they live inline in
 * KingOfTheMountain ("unified 56px strip with hairline divider, matches Team
 * Performance side") and were copied by hand into the Team Performance side.
 * Never extracted, so every card built afterwards invented its own header —
 * 53px instead of 56, a tinted strip instead of a transparent one, 12px of
 * padding instead of flush-to-the-edge, or no strip at all. Four cards, four
 * headers, on one board.
 *
 * So it lives here now. Any card that wants the house header uses this and
 * inherits changes to it; nothing gets to drift again.
 *
 *   • 56px tall, flush to the card's edges (the card itself carries no padding)
 *   • hairline #EAECF0 divider under it, 12px of air below that
 *   • title: DM Sans 16/600, -0.01em, #0F172A
 *   • inside a dashboard's placed component it keeps 52px clear on the right for
 *     the floating "…" options button
 *   • `right` is the slot for a card's own controls — a period label, a player
 *     dropdown, a status pill — vertically centred against the title
 */
const CardHeader = ({ title, right = null, sx = {} }) => (
  <SoftBox
    className="gmbw-cardhead"
    sx={{
      flex: "0 0 auto",
      height: "56px",
      paddingLeft: { mini: "16px", sm: "20px" },
      paddingRight: { mini: "16px", sm: "20px" },
      borderBottom: "1px solid #EAECF0",
      marginBottom: "12px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "10px",
      /* The Analytics board floats a "…" button over a placed component's
         top-right corner (10px in, 28px wide). Reserve its width so the card's
         own controls (player picker, period pill) never slide underneath it.
         Done here so every card on the house header inherits it. */
      ".wc-comp-body &": { paddingRight: "52px" },
      ...sx,
    }}
  >
    <span
      style={{
        fontFamily: '"DM Sans", system-ui, sans-serif',
        fontSize: "16px",
        fontWeight: 600,
        letterSpacing: "-0.01em",
        color: "#0F172A",
        lineHeight: 1.2,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {title}
    </span>
    {right && (
      <SoftBox sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: "8px" }}>
        {right}
      </SoftBox>
    )}
  </SoftBox>
);

/** The card body that goes under a CardHeader: the header is flush, so the
    padding lives here. Matches the 20px side / 16px bottom the board uses. */
export const CARD_BODY_PAD = { mini: "0 16px 16px", sm: "0 20px 16px" };

/** A card root that hosts CardHeader + body — white, 8px, no padding of its own. */
export const CARD_ROOT = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  background: "#FFFFFF",
  borderRadius: "8px",
  overflow: "hidden",
  fontFamily: '"DM Sans", system-ui, sans-serif',
};

export default CardHeader;
